import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Zap, AlertTriangle, CreditCard } from "lucide-react";
import { useGetHomeQuery, useSetupAutoTopUpMutation, useGetPaymentMethodsQuery } from "@/store/rest_api";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

export function AutoTopUpSettings() {
  const { data: user, isLoading: loadingUser } = useGetHomeQuery();
  const { data: paymentMethods, isLoading: loadingPaymentMethods } = useGetPaymentMethodsQuery();
  const [setupAutoTopUp, { isLoading: isSaving }] = useSetupAutoTopUpMutation();

  const organisation = user?.activeOrganisation;
  const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;

  const [enabled, setEnabled] = useState(false);
  const [amount, setAmount] = useState("50");
  const [threshold, setThreshold] = useState("10");

  useEffect(() => {
    if (organisation) {
      setEnabled(organisation.automaticTopUp || false);
      setAmount(organisation.automaticTopUpAmount?.toString() || "50");
      setThreshold(organisation.automaticTopUpThreshold?.toString() || "10");
    }
  }, [organisation]);

  const handleSave = async () => {
    const amountNum = parseFloat(amount);
    const thresholdNum = parseFloat(threshold);

    if (enabled) {
      if (amountNum < 10) {
        toast.error("Minimum auto top-up amount is $10");
        return;
      }

      if (thresholdNum < 5) {
          toast.error("Minimum threshold is $5");
        return;
      }

      if (!hasPaymentMethods) {
        toast.error("Please add a payment method before enabling auto top-up");
        return;
      }
    }

    try {
      await setupAutoTopUp({
        enabled,
        amount: enabled ? amountNum : undefined,
        threshold: enabled ? thresholdNum : undefined,
      }).unwrap();
      toast.success(`Auto top-up has been ${enabled ? "enabled" : "disabled"}`);
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update settings");
    }
  };

  if (loadingUser || loadingPaymentMethods) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Auto Top-Up</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  const hasChanges =
    enabled !== organisation?.automaticTopUp ||
    (enabled &&
      (amount !== organisation?.automaticTopUpAmount?.toString() ||
        threshold !== organisation?.automaticTopUpThreshold?.toString()));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Auto Top-Up
        </CardTitle>
        <CardDescription>Automatically add credits when your balance drops below a threshold</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="auto-topup-enabled" className="text-base">
              Enable Auto Top-Up
            </Label>
            <p className="text-sm text-muted-foreground">
              {enabled ? "Auto top-up is active" : "Auto top-up is disabled"}
            </p>
          </div>
          <Switch id="auto-topup-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {!hasPaymentMethods && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>You need to add a payment method before enabling auto top-up.</AlertDescription>
          </Alert>
        )}

        {enabled && (
          <>
            <div className="space-y-2">
              <Label htmlFor="threshold">Trigger Threshold</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="threshold"
                  type="number"
                  min="5"
                  step="0.01"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  className="pl-7"
                  placeholder="10.00"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Auto top-up will trigger when your balance drops below this amount (minimum $5)
              </p>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">Top-Up Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="amount"
                  type="number"
                  min="10"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-7"
                  placeholder="50.00"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Amount to add to your balance when triggered (minimum $10)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Quick Presets</Label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { threshold: 10, amount: 25 },
                  { threshold: 10, amount: 50 },
                  { threshold: 20, amount: 100 },
                  { threshold: 50, amount: 250 },
                ].map((preset) => (
                  <Button
                    key={`${preset.threshold}-${preset.amount}`}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setThreshold(preset.threshold.toString());
                      setAmount(preset.amount.toString());
                    }}
                  >
                    ${preset.threshold} → ${preset.amount}
                  </Button>
                ))}
              </div>
            </div>

            {/* Example */}
            <Alert>
              <CreditCard className="h-4 w-4" />
              <AlertDescription>
                <strong>Example:</strong> When your balance drops below ${threshold}, we'll automatically charge your
                card ${amount} and add it to your account.
              </AlertDescription>
            </Alert>
          </>
        )}

        <Button
          onClick={handleSave}
          disabled={isSaving || !hasChanges || (enabled && !hasPaymentMethods)}
          className="w-full"
        >
          {isSaving ? "Saving..." : "Save Settings"}
        </Button>

        {organisation?.automaticTopUp && !enabled && (
          <p className="text-xs text-muted-foreground text-center">
            Auto top-up is currently enabled with ${organisation.automaticTopUpAmount} at $
            {organisation.automaticTopUpThreshold} threshold
          </p>
        )}
      </CardContent>
    </Card>
  );
}
